import type {
  APIResponse,
  ApplicationStudioArtifactKind,
  ApplicationStudioArtifactSummary,
  ApplicationStudioCreateProjectInput,
  ApplicationStudioGenerateInput,
  ApplicationStudioGenerateResult,
  ApplicationStudioPrepareApplyResult,
  ApplicationStudioProjectSummary,
  ApplicationStudioSubmitApplyInput,
  ApplicationStudioSubmitApplyResult,
  ApplicationStudioApplyInput,
  EntitlementDecision,
} from '@tabbio/types';
import { api } from './api';

export class ApplicationStudioApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApplicationStudioApiError';
  }

  get decision(): EntitlementDecision | undefined {
    return this.meta?.decision as EntitlementDecision | undefined;
  }
}

export function isApplicationStudioEntitlementDenied(
  error: unknown,
): error is ApplicationStudioApiError {
  return (
    error instanceof ApplicationStudioApiError &&
    error.code === 'SUBSCRIPTION_ENTITLEMENT_DENIED'
  );
}

function unwrap<T>(response: APIResponse<T>) {
  if (response.error) {
    throw new ApplicationStudioApiError(
      response.error.message,
      response.error.code,
      response.meta as Record<string, unknown> | undefined,
    );
  }
  if (response.data === null || response.data === undefined) {
    throw new Error('Empty response');
  }
  return response.data;
}

function query(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

export async function fetchApplicationStudioProjects() {
  return unwrap(
    await api.get<ApplicationStudioProjectSummary[]>(
      '/api/application-studio/projects',
    ),
  );
}

export async function createApplicationStudioProject(
  input: ApplicationStudioCreateProjectInput,
) {
  return unwrap(
    await api.post<ApplicationStudioProjectSummary>(
      '/api/application-studio/projects',
      input,
    ),
  );
}

export async function fetchApplicationStudioProject(projectId: string) {
  return unwrap(
    await api.get<ApplicationStudioProjectSummary>(
      `/api/application-studio/projects/${projectId}`,
    ),
  );
}

export async function fetchApplicationStudioArtifacts(input: {
  projectId?: string;
  kind?: ApplicationStudioArtifactKind;
  limit?: number;
} = {}) {
  const path = input.projectId
    ? `/api/application-studio/projects/${input.projectId}/artifacts`
    : '/api/application-studio/artifacts';
  return unwrap(
    await api.get<ApplicationStudioArtifactSummary[]>(
      `${path}${query({ kind: input.kind, limit: input.limit })}`,
    ),
  );
}

export async function fetchApplicationStudioArtifact(artifactId: string) {
  return unwrap(
    await api.get<ApplicationStudioArtifactSummary>(
      `/api/application-studio/artifacts/${artifactId}`,
    ),
  );
}

export async function generateApplicationStudioArtifact(
  projectId: string,
  input: ApplicationStudioGenerateInput,
) {
  return unwrap(
    await api.post<ApplicationStudioGenerateResult>(
      `/api/application-studio/projects/${projectId}/generate`,
      input,
    ),
  );
}

export async function updateApplicationStudioArtifact(
  artifactId: string,
  input: {
    title?: string | null;
    content?: Record<string, unknown>;
    plaintext?: string | null;
    status?: 'ready' | 'draft' | 'failed';
  },
) {
  return unwrap(
    await api.patch<ApplicationStudioArtifactSummary>(
      `/api/application-studio/artifacts/${artifactId}`,
      input,
    ),
  );
}

export async function deleteApplicationStudioArtifact(artifactId: string) {
  return unwrap(
    await api.delete<{ id: string }>(
      `/api/application-studio/artifacts/${artifactId}`,
    ),
  );
}

export async function regenerateApplicationStudioArtifact(artifactId: string) {
  return unwrap(
    await api.post<ApplicationStudioGenerateResult>(
      `/api/application-studio/artifacts/${artifactId}/regenerate`,
    ),
  );
}

export async function prepareApplicationStudioApply(
  projectId: string,
  input: ApplicationStudioApplyInput,
) {
  return unwrap(
    await api.post<ApplicationStudioPrepareApplyResult>(
      `/api/application-studio/projects/${projectId}/prepare-apply`,
      input,
    ),
  );
}

export async function submitApplicationStudioApply(
  projectId: string,
  input: ApplicationStudioSubmitApplyInput,
) {
  return unwrap(
    await api.post<ApplicationStudioSubmitApplyResult>(
      `/api/application-studio/projects/${projectId}/submit-apply`,
      input,
    ),
  );
}
